import React, { FC, useEffect, useState, useMemo } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../store";
import {
  fetchPlayersWithRegistrations,
  getPlayersWithRegistrations,
  getPlayersWithRegistrationsLoading,
} from "../../../features/player/playerSlice";
import { useLocation } from "react-router-dom";
import { createCsvBlob } from "../../../utils/arrayHelper";

interface RegistrationsProps {}

const Registrations: FC<RegistrationsProps> = () => {
  const dispatch = useAppDispatch();
  const playersWithRegistrations = useAppSelector(getPlayersWithRegistrations);
  const loading = useAppSelector(getPlayersWithRegistrationsLoading);
  const location = useLocation();

  const [searchTerm, setSearchTerm] = useState<string>("");
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortAsc, setSortAsc] = useState(true);

  useEffect(() => {
    dispatch(fetchPlayersWithRegistrations());
  }, [dispatch, location.pathname]);

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortAsc(!sortAsc);
    } else {
      setSortField(field);
      setSortAsc(true);
    }
  };

  const filteredAndSortedPlayers = useMemo(() => {
    const filtered = playersWithRegistrations.filter((p) =>
      `${p.name} ${p.surname} ${p.club}`
        .toLowerCase()
        .includes(searchTerm.toLowerCase())
    );

    if (!sortField) return filtered;

    return [...filtered].sort((a, b) => {
      let valA: string | number = "";
      let valB: string | number = "";

      switch (sortField) {
        case "name":
          valA = `${a.name} ${a.surname}`;
          valB = `${b.name} ${b.surname}`;
          break;
        case "dossard":
          valA = a.dossard || 0;
          valB = b.dossard || 0;
          break;
        case "points":
          valA = a.points;
          valB = b.points;
          break;
        case "club":
          valA = a.club || "";
          valB = b.club || "";
          break;
        default:
          break;
      }

      // Tri numérique pour les points et dossards, alphabétique pour les autres
      if (sortField === "points" || sortField === "dossard") {
        return (Number(valA) - Number(valB)) * (sortAsc ? 1 : -1);
      } else {
        return (valA > valB ? 1 : -1) * (sortAsc ? 1 : -1);
      }
    });
  }, [playersWithRegistrations, searchTerm, sortField, sortAsc]);

  // Vérifier si au moins un joueur a un dossard
  const hasAnyDossard = useMemo(() => {
    return playersWithRegistrations.some((p) => p.dossard);
  }, [playersWithRegistrations]);

  const handleExport = () => {
    const headers = [
      "Nom",
      ...(hasAnyDossard ? ["Dossard"] : []),
      "Points",
      "Licence",
      "Club",
      "Catégorie",
      "Sexe",
      "Email",
      "Tableaux",
    ];

    const rows = filteredAndSortedPlayers.map((p) => [
      `${p.name} ${p.surname}`,
      ...(hasAnyDossard ? [p.dossard || ""] : []),
      p.points,
      p.licenceId,
      p.club,
      p.categorie,
      p.sexe,
      p.participation?.[0]?.email || "",
      p.participation?.map((par) => par.tableau.name).join(" "),
    ]);

    const blob = createCsvBlob(headers, rows);
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `joueurs_inscriptions_${Date.now()}.csv`;
    link.click();
  };

  if (loading) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{ height: "80vh" }}
      >
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <Box
      sx={{
        maxWidth: "100%",
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <Typography variant="h5" fontWeight={600}>
        Liste des joueurs avec inscriptions
      </Typography>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems="center"
      >
        <TextField
          size="small"
          label="Recherche par Nom ou Club"
          variant="outlined"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ width: 300 }}
        />

        <Button variant="contained" onClick={handleExport}>
          Exporter CSV
        </Button>
      </Stack>

      <TableContainer component={Paper} sx={{ flexGrow: 1, overflow: "auto" }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {[
                { label: "Nom", field: "name" },
                ...(hasAnyDossard
                  ? [{ label: "Dossard", field: "dossard" }]
                  : []),
                { label: "Points", field: "points" },
                { label: "Licence" },
                { label: "Club", field: "club" },
                { label: "Catégorie" },
                { label: "Sexe" },
                { label: "Email" },
                { label: "Tableaux" },
              ].map((col) => (
                <TableCell
                  key={col.label}
                  onClick={col.field ? () => handleSort(col.field) : undefined}
                  sx={{
                    cursor: col.field ? "pointer" : "default",
                    fontWeight: col.field ? 600 : undefined,
                  }}
                >
                  {col.label}
                  {sortField === col.field && (sortAsc ? " ▲" : " ▼")}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredAndSortedPlayers.length > 0 ? (
              filteredAndSortedPlayers.map((p) => (
                <TableRow key={p.id} hover>
                  <TableCell>{`${p.name} ${p.surname}`}</TableCell>
                  {hasAnyDossard && <TableCell>{p.dossard || ""}</TableCell>}
                  <TableCell>{p.points}</TableCell>
                  <TableCell>{p.licenceId}</TableCell>
                  <TableCell>{p.club}</TableCell>
                  <TableCell>{p.categorie}</TableCell>
                  <TableCell>{p.sexe}</TableCell>
                  <TableCell>{p.participation?.[0]?.email || ""}</TableCell>
                  <TableCell>
                    {p.participation?.map((par) => par.tableau.name).join(" ")}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={hasAnyDossard ? 9 : 8}
                  align="center"
                  sx={{ py: 4, fontStyle: "italic", color: "text.secondary" }}
                >
                  Aucun joueur trouvé.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default Registrations;
